<template>
  <div :class="['app-root', { 'is-preview-env': isPreviewEnv }]">
    <div v-if="isPreviewEnv" class="watermark-overlay-global" aria-hidden="true"></div>
    <AppNavbar
      :show-menu="isAuthenticated"
      :show-notifications="isAuthenticated"
      :show-language-selector="!isAuthenticated"
    />
    <CookieConsentBanner />
    <button
      v-if="shouldShowGlobalFullscreenButton"
      type="button"
      class="app-fullscreen-btn"
      :title="fullscreenTitle"
      :aria-label="fullscreenTitle"
      @click="toggleFullscreen"
    >
      <i :class="isFullscreen ? 'fa-solid fa-down-left-and-up-right-to-center' : 'fa-solid fa-up-right-and-down-left-from-center'"></i>
    </button>
    <main class="app-main mt-4 pt-5">
      <router-view />
      <SessionTimeoutManager v-if="shouldShowSessionTimeout" />
      <loading-modal ref="globalLoading"></loading-modal>
    </main>
    <AppFooter />
    <div
      v-if="isPreviewEnv && showTestAlert"
      class="alert alert-danger test-env-alert"
      role="alert"
    >
      <span class="test-env-text">{{ $t('auth.test_env_notice') }}</span>
      <a
        class="alert-link"
        href="https://slackline-tjs.com"
        target="_blank"
        rel="noopener noreferrer"
        hidden="true"
      >
        {{ $t('auth.test_env_link') }}
      </a>
      <button
        type="button"
        class="test-env-close"
        aria-label="Close test environment notice"
        @click="dismissTestAlert"
      >
        x
      </button>
    </div>
  </div>
</template>


<script>
import AppNavbar from "@/components/app/AppNavbar.vue";
import AppFooter from "@/components/app/AppFooter.vue";
import CookieConsentBanner from "@/components/app/CookieConsentBanner.vue";
import {mapActions, mapState} from "vuex";
import LoadingModal from "@/services/LoadingModal.vue";
import SessionTimeoutManager from "@/components/app/SessionTimeoutManager.vue";
import { isPreviewEnv as isPreviewEnvHelper } from "@/services/hostContext";
import echoService from "@/services/echoService";

export default {
  components: { AppNavbar, AppFooter, CookieConsentBanner, LoadingModal, SessionTimeoutManager },
  data() {
    return {
      showTestAlert: true,
      isFullscreen: false,
      fullscreenSupported: false,
      tricklistChannel: null,
      tricklistRefreshPromise: null,
    };
  },
  computed: {
    ...mapState('user', ['isAuthenticated']),
    isPreviewEnv() {
      return isPreviewEnvHelper(this.$hostContext && this.$hostContext.env);
    },
    shouldShowSessionTimeout() {
      const path = this.$route?.path || '';
      const isTimerCode = path.startsWith('/timer-code');
      return this.isAuthenticated && !isTimerCode;
    },
    shouldShowGlobalFullscreenButton() {
      const path = this.$route?.path || '';
      return this.fullscreenSupported && !path.startsWith('/timer-code');
    },
    fullscreenTitle() {
      return this.isFullscreen
        ? (this.$t('common.exit_fullscreen') || 'Salir de pantalla completa')
        : (this.$t('common.fullscreen') || 'Pantalla completa');
    },
  },
  watch: {
    isAuthenticated: {
      immediate: true,
      handler(isAuthenticated) {
        if (isAuthenticated) {
          this.subscribeTricklistChannel();
          return;
        }

        this.unsubscribeTricklistChannel();
      },
    },
  },
  mounted() {
    this.hydrateConsent();
    this.fullscreenSupported = this.checkFullscreenSupport();
    this.handleFullscreenChange();
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);
  },
  beforeUnmount() {
    this.unsubscribeTricklistChannel();
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
  },
  methods: {
    ...mapActions('cookieConsent', ['hydrateConsent']),
    ...mapActions('user', ['refreshTricks']),
    dismissTestAlert() {
      this.showTestAlert = false;
    },
    subscribeTricklistChannel() {
      if (!this.isAuthenticated || this.tricklistChannel) {
        return;
      }

      this.tricklistChannel = echoService.channel('tricks.global');
      this.tricklistChannel.listen('.TricklistUpdated', this.handleTricklistUpdated);
    },
    unsubscribeTricklistChannel() {
      if (this.tricklistChannel) {
        this.tricklistChannel.stopListening('.TricklistUpdated');
        this.tricklistChannel = null;
      }

      echoService.leave('tricks.global');
    },
    async handleTricklistUpdated(event) {
      if (!this.isAuthenticated) {
        return;
      }

      try {
        if (this.tricklistRefreshPromise) {
          await this.tricklistRefreshPromise;
        }

        this.tricklistRefreshPromise = this.refreshTricks();
        await this.tricklistRefreshPromise;
      } catch (error) {
        console.warn('tricklist realtime refresh failed', error?.response || error);
      } finally {
        this.tricklistRefreshPromise = null;
      }

      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function' && typeof CustomEvent === 'function') {
        window.dispatchEvent(new CustomEvent('tricks:updated', {
          detail: event || {},
        }));
      }
    },
    checkFullscreenSupport() {
      return Boolean(
        document.fullscreenEnabled
        || document.webkitFullscreenEnabled
        || document.documentElement?.requestFullscreen
        || document.documentElement?.webkitRequestFullscreen
      );
    },
    getFullscreenElement() {
      return document.fullscreenElement || document.webkitFullscreenElement || null;
    },
    async requestAppFullscreen() {
      const element = document.documentElement;

      if (element.requestFullscreen) {
        await element.requestFullscreen();
        return;
      }

      if (element.webkitRequestFullscreen) {
        await element.webkitRequestFullscreen();
      }
    },
    async exitAppFullscreen() {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
        return;
      }

      if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen();
      }
    },
    async toggleFullscreen() {
      try {
        if (!this.getFullscreenElement()) {
          await this.requestAppFullscreen();
        } else {
          await this.exitAppFullscreen();
        }
      } catch (error) {
        console.warn('fullscreen toggle failed', error?.message || error);
      }
    },
    handleFullscreenChange() {
      this.isFullscreen = Boolean(this.getFullscreenElement());
    }
  }
};
</script>

<style>
.app-root {
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.app-main {
  flex: 1 0 auto;
}

.app-fullscreen-btn {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 1150;
  width: 44px;
  height: 44px;
  border: 1px solid rgba(15, 23, 42, 0.18);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.94);
  color: #0f172a;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.14);
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.app-fullscreen-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.18);
  background: #ffffff;
}

.app-fullscreen-btn:focus-visible {
  outline: 3px solid rgba(37, 99, 235, 0.28);
  outline-offset: 2px;
}

.box_component {
  width: 80% !important;
  max-width: 80% !important;
}

@media (max-width: 576px) {
  .box_component {
    width: 100% !important;
    max-width: 100% !important;
  }
}

.watermark-overlay-global {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: -1;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='240' height='240' viewBox='0 0 200 200'%3E%3Ctext x='0' y='120' fill='rgba(0,0,0,0.06)' font-size='32' transform='rotate(-30 100 100)'%3ETest Area%3C/text%3E%3C/svg%3E");
  background-repeat: repeat;
  background-size: 240px 240px;
  opacity: 1;
}

.test-env-alert {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  border-radius: 0;
  margin: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  gap: 12px;
  padding-right: 48px;
}

.test-env-text {
  flex: 1;
}

.test-env-close {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: inherit;
  font-size: 18px;
  cursor: pointer;
}

.app-root.is-preview-env main {
  padding-bottom: 80px;
}

.app-root.is-preview-env .app-fullscreen-btn {
  bottom: 72px;
}
</style>
